import { DatePipe } from '@angular/common';
import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    ViewChildren
} from '@angular/core';
import {
    ApiDefaults,
    ApiService,
    LsqApiServiceListSource
} from '@lsq-frontend/angular-shared/api-service';
import {
    DateRangeFilterRequest,
    FilterRequest,
    ListResponse
} from '@lsq-frontend/remote-services/enterprise/common';
import { Observable } from 'rxjs';
import { distinctUntilChanged, startWith, take } from 'rxjs/operators';
import { Messages } from '../../../constants/messages.constants';
import { DateFormat } from '../../../constants/date-format.constants';
import { Table } from '../../../constants/table.constants';
import { Key, SubscriptionLike } from '../../../models/common';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { CheckableRowsService } from '../../../services/table/checkable-rows/checkable-rows.service';
import { CheckableRowsServiceFactory } from '../../../services/table/checkable-rows/checkable-rows.service.factory';
import { ExpandableRowsService } from '../../../services/table/expandable-rows/expandable-rows.service';
import { ExpandableRowsServiceFactory } from '../../../services/table/expandable-rows/expandable-rows.service.factory';
import { WindowReferenceService } from '../../../services/window-reference/window-reference.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';
import { TableGroup } from '../../models/table-group';
import { ITableRowErrorMessageConfig } from '../../models/table-row-error-message-config';

export interface TotalsRowKeyPair {
    columnKey: string;
    totalKey: string;
}

@Directive()
@LifecycleAware()
export abstract class LsqGroupedDataTableComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('normalGroupHeading') public normalGroupHeadingsQuery: QueryList<ElementRef>;
    @ViewChildren('stickyGroupHeading') public stickyGroupHeadingsQuery: QueryList<ElementRef>;
    @Input() public dataSource: LsqApiServiceListSource<any, any>;
    @Input() public groupBy: string;
    @Input() public rowPrimaryKey?: string; // Required if rows are checkable.
    @Input() public rowsAreCheckable = false;
    @Input() public takeOptions = Table.DefaultTakeOptions;
    @Input() public noDataMessage = Messages.NoResultsFound;
    @Input() public showGroupHeading = true;
    @Input() public hasHeader = true;
    @Input() public hasStickyHeader = true;
    @Input() public hasStickyGroupHeadings = true;
    @Input() public shouldExpandFirstRow = true;
    @Input() public rowErrorMessageConfigs?: ITableRowErrorMessageConfig[];
    @Input() public getKeysByDateService: ApiService<FilterRequest, ListResponse<any>>;
    @Input() public getKeysByDateBaseRequest?: FilterRequest;
    @Input() public shouldShowCheckbox?: (row: any) => boolean;
    @Input() public shouldRowExpand?: (row: any) => boolean;
    @Input() public isPaginated = true;

    @Output() public rowCheck = new EventEmitter<Key[]>();
    @Output() public rowExpand = new EventEmitter<object[]>();

    // State.
    public groups: TableGroup[] = [];
    public allRowsInDateRangeAreChecked = false;
    public isSmallDeviceStream: Observable<boolean>;
    public _hasScrolled = false;
    private _preventViewUpdates = false;
    public groupHeadingsIsStuckStates: boolean[];
    public leftHeadingPosition: number;

    public totalsRowKeyPairs: TotalsRowKeyPair[];

    // Subscriptions.
    private _windowScrollSubscription: SubscriptionLike;

    // Services.
    public checkableRowsService: CheckableRowsService;
    public expandableRowsService: ExpandableRowsService;

    // Constants.
    public defaultTake = ApiDefaults.TakeDefault;

    constructor(
        private _checkableRowsServiceFactory: CheckableRowsServiceFactory,
        private _expandableRowsServiceFactory: ExpandableRowsServiceFactory,
        private _windowReferenceService: WindowReferenceService,
        private _responsiveService: ResponsiveService,
        private _datePipe: DatePipe
    ) {}

    public ngOnInit(): void {
        this.totalsRowKeyPairs = this.setTotalsRowKeyPairs();

        if (this.rowsAreCheckable) {
            this.checkableRowsService = this._checkableRowsServiceFactory.create();

            this.checkableRowsService.checkedRowsSubject
                .pipe(forLifeOf(this))
                .subscribe((rows) => this.rowCheck.emit(rows));
        }

        this.expandableRowsService = this._expandableRowsServiceFactory.create();

        this.expandableRowsService.rowsExpandedSubject.pipe(forLifeOf(this)).subscribe((rows) => {
            this.rowExpand.emit(rows);
            // This fixes a `ExpressionChangedAfterItHasBeenCheckedError` when scrolled at the bottom of a page.
            // A lot of actions happen so a normal execute after render does not work in all cases.
            if (window.innerHeight + window.pageYOffset >= document.body.offsetHeight) {
                this._preventViewUpdates = true;
                setTimeout(() => {
                    this._preventViewUpdates = false;
                }, 10);
            }
        });

        this._windowScrollSubscription = this._windowReferenceService
            .createScrollStream(true)
            .subscribe(() => {
                this._updateGroupHeadingStickyStates();
                if (!this._hasScrolled) {
                    Utilities.executeAfterRender(() => {
                        // This prevents an `ExpressionChangedAfterItHasBeenCheckedError` when the page auto scrolls on reload
                        this._hasScrolled = true;
                    });
                }
            });

        this._windowReferenceService.resizeStream.pipe(forLifeOf(this)).subscribe(() => {
            this._updateGroupHeadingStickyStates();
        });

        this.isSmallDeviceStream = this._responsiveService.smBelow.pipe(
            forLifeOf(this),
            distinctUntilChanged()
        );

        if (this.dataSource.rows$) {
            this.dataSource.rows$.pipe(forLifeOf(this)).subscribe((rows) => {
                this.handleResponse(rows);
            });
        } else {
            this.dataSource.data$.pipe(forLifeOf(this)).subscribe((response) => {
                this.handleResponse(response);
            });
        }
    }

    public ngAfterViewInit(): void {
        this.normalGroupHeadingsQuery.changes.pipe(forLifeOf(this), startWith()).subscribe(() => {
            this._updateGroupHeadingStickyStates();
        });
    }

    public abstract setTotalsRowKeyPairs(): TotalsRowKeyPair[];

    public ngOnDestroy(): void {
        if (this._windowScrollSubscription) {
            this._windowScrollSubscription.unsubscribe();
        }
    }

    public clearSelectedChecks(request?: DateRangeFilterRequest | FilterRequest): void {
        if (
            this.requestIsDateRange() &&
            ((this.dataSource.request as DateRangeFilterRequest).fromDateFilter !==
                (request as DateRangeFilterRequest).fromDateFilter ||
                (this.dataSource.request as DateRangeFilterRequest).toDateFilter !==
                    (request as DateRangeFilterRequest).toDateFilter)
        ) {
            this.allRowsInDateRangeAreChecked = false;
            this.checkableRowsService.reset();
        }
    }

    // Boolean methods.
    public requestIsDateRange(): boolean {
        return (
            this.dataSource.request &&
            !!(this.dataSource.request as DateRangeFilterRequest).fromDateFilter &&
            !!(this.dataSource.request as DateRangeFilterRequest).toDateFilter
        );
    }

    public isGroupedByDate(): boolean {
        return !!this.groupBy.match(/(Date$|^date|createdAt|updatedAt)/);
    }

    // Getters.
    public getRequestFromDateFilter(): Date {
        return !!this.dataSource.request
            ? (this.dataSource.request as DateRangeFilterRequest).fromDateFilter
            : null;
    }

    public getRequestToDateFilter(): Date {
        return !!this.dataSource.request
            ? (this.dataSource.request as DateRangeFilterRequest).toDateFilter
            : null;
    }

    public getErrorMessageTemplateRef(row: object): TemplateRef<any> {
        if (!this.rowErrorMessageConfigs || !this.rowErrorMessageConfigs.length) {
            return null;
        }
        const config = this.rowErrorMessageConfigs.find((_config) => row[_config.flagKey]);
        if (!!config) {
            return config.templateRef;
        }
        return null;
    }

    // Handle responses.
    public handleResponse(responseOrRows?: ListResponse<any> | any[]): void {
        const rows = Array.isArray(responseOrRows)
            ? (responseOrRows as any[])
            : responseOrRows.rows;

        if (this.rowsAreCheckable) {
            this.checkableRowsService.addRows(rows.map((row) => row[this.rowPrimaryKey]));
        }

        this.groupData(rows);
        this._windowReferenceService.scrollToTop();

        if (this.allRowsInDateRangeAreChecked) {
            Utilities.executeAfterRender(() => {
                this.handleSelectAll(true);
            });
        }

        this.isSmallDeviceStream.pipe(take(1)).subscribe((isSmallDevice) => {
            if (this.shouldExpandFirstRow && rows && rows[0] && !isSmallDevice) {
                this.expandableRowsService.expand(rows[0]);
            }
        });
    }

    // Handle grouping.
    public async groupData(rows: object[]): Promise<void> {
        let groups: TableGroup[] = [];
        const groupMap: { [key: string]: TableGroup } = {};
        this._preventViewUpdates = true;
        this.groupHeadingsIsStuckStates = null;

        // Transform rows into groups.
        rows.forEach((row: any) => {
            const rowValue = row[this.groupBy];
            let groupByValue =
                rowValue instanceof Date
                    ? this._datePipe.transform(rowValue, DateFormat.LsqShortYear)
                    : rowValue;

            if (typeof groupByValue === 'number') {
                groupByValue = (groupByValue as number).toString();
            }

            const groupToAssign = groupMap[groupByValue];

            if (groupToAssign) {
                groupToAssign.rows.push(row);
            } else {
                const newGroup = new TableGroup(groupByValue);
                newGroup.rows.push(row);
                if (this.checkableRowsService && this.rowsAreCheckable) {
                    const isGroupchecked = this.checkableRowsService.areAllRowsInGroupChecked(
                        newGroup.heading
                    );
                    newGroup.allRowsAreChecked = isGroupchecked;
                }
                groups.push(newGroup);
                groupMap[groupByValue] = newGroup;
            }
        });

        // If 'Select All' is checked, check all the rows.
        if (this.allRowsInDateRangeAreChecked) {
            this.handleSelectAll(true);
        }

        // Create 'Total' rows.
        if (this.totalsRowKeyPairs) {
            groups = groups.map((group) => {
                // For each group, create a 'Total' row.
                const newGroup: TableGroup = { ...group };
                // Reduce the group's rows down to a 'Total' row.
                newGroup.totalsRow = group.rows.reduce((accumulator, current) => {
                    const acc = { ...accumulator };
                    this.totalsRowKeyPairs.forEach((pair) => {
                        if (typeof acc[pair.totalKey] !== 'number') {
                            acc[pair.totalKey] = 0;
                        }
                        if (!current[pair.columnKey]) {
                            current[pair.columnKey] = 0;
                        }
                        acc[pair.totalKey] = parseFloat(
                            (acc[pair.totalKey] + current[pair.columnKey]).toFixed(2)
                        );
                    });
                    return acc;
                }, group.rows[0]);

                return newGroup;
            });
        }

        if (this.rowsAreCheckable) {
            // Load up checkable rows cache.
            groups.forEach(async (group, index) => {
                if (index === 0 || index === groups.length - 1) {
                    let request = new DateRangeFilterRequest({
                        take: -1,
                        fromDateFilter: group.rows[0][this.groupBy],
                        toDateFilter: group.rows[0][this.groupBy]
                    });

                    // populate the base request fields if they exist.
                    if (this.getKeysByDateBaseRequest) {
                        request = {
                            ...this.getKeysByDateBaseRequest,
                            ...request
                        };
                    }

                    const response = await this.getKeysByDateService.executeOnce(request);

                    this.checkableRowsService.addGroupCache(group.heading, response.rows);
                } else {
                    this.checkableRowsService.addGroupCache(
                        group.heading,
                        group.rows
                            .filter((row) => this.areRowsCheckable(row))
                            .map((row) => row[this.rowPrimaryKey])
                    );
                }
            });
        }

        this.groups = groups;

        // Prevent `ExpressionChangedAfterItHasBeenCheckedError`.
        Utilities.executeAfterRender(() => {
            this.groupHeadingsIsStuckStates = this.normalGroupHeadingsQuery.map((_) => false);
            this._preventViewUpdates = false;
        });
    }

    // Event handlers.
    public handleRowCheck(isChecked: boolean, group: TableGroup): void {
        if (!isChecked) {
            this.allRowsInDateRangeAreChecked = false;
            if (group) {
                group.allRowsAreChecked = false;
            }
        } else {
            this._checkGroupCheckStatus(group);
        }
    }

    private _checkGroupCheckStatus(group: TableGroup): void {
        if (this.checkableRowsService.areAllRowsInGroupChecked(group.heading)) {
            group.allRowsAreChecked = true;
        }
    }

    public async handleGroupCheck(
        group: TableGroup,
        isChecked: boolean,
        groupIndex: number
    ): Promise<void> {
        if (group && group.rows && group.rows[0]) {
            group.allRowsAreChecked = isChecked;
            const keysToModify = group.rows
                .filter((row) => this.areRowsCheckable(row))
                .map((row) => row[this.rowPrimaryKey]);

            if (
                this.getKeysByDateService &&
                this.isGroupedByDate() &&
                (groupIndex === 0 || groupIndex === this.groups.length - 1)
            ) {
                let request = new DateRangeFilterRequest({
                    take: -1,
                    fromDateFilter: group.rows[0][this.groupBy],
                    toDateFilter: group.rows[0][this.groupBy]
                });

                // populate the base request fields if they exist.
                if (this.getKeysByDateBaseRequest) {
                    request = {
                        ...this.getKeysByDateBaseRequest,
                        ...request
                    };
                }

                const response = await this.getKeysByDateService.executeOnce(request);
                if (isChecked && response.rows.length > 0) {
                    this.checkableRowsService.addRows(response.rows);
                    this.checkableRowsService.checkMany(response.rows);
                } else {
                    this.checkableRowsService.addRows(response.rows);
                    this.checkableRowsService.uncheckMany(response.rows);
                }
            }

            if (isChecked) {
                this.checkableRowsService.checkMany(keysToModify);
            } else {
                this.checkableRowsService.uncheckMany(keysToModify);
                this.allRowsInDateRangeAreChecked = false;
            }
        }
    }

    public async handleSelectAll(isChecked: boolean): Promise<void> {
        this.allRowsInDateRangeAreChecked = isChecked;
        this.groups = this.groups.map((group) => {
            group.allRowsAreChecked = isChecked;
            return group;
        });

        if (isChecked) {
            if (this.getKeysByDateService && this.dataSource.request) {
                let request = new DateRangeFilterRequest({
                    take: -1,
                    fromDateFilter: (this.dataSource.request as DateRangeFilterRequest)
                        .fromDateFilter,
                    toDateFilter: (this.dataSource.request as DateRangeFilterRequest).toDateFilter
                });

                // populate the base request fields if they exist.
                if (this.getKeysByDateBaseRequest) {
                    request = {
                        ...this.getKeysByDateBaseRequest,
                        ...request
                    };
                }

                const response = await this.getKeysByDateService.executeOnce(request);
                if (response.rows.length > 0) {
                    this.checkableRowsService.addRows(response.rows);
                    this.checkableRowsService.checkMany(response.rows);
                }
            }
        } else {
            this.checkableRowsService.uncheckAll();
        }
    }

    public handleMobileTableCardFooterClick(row: any): void {
        this.expandableRowsService.expand(row);
    }

    private _updateGroupHeadingStickyStates(): void {
        if (this._preventViewUpdates) {
            return;
        }

        const _normalGroupHeadings = this.stickyGroupHeadingsQuery.map((el) => el.nativeElement);
        const _stickyGroupHeadings = this.normalGroupHeadingsQuery.map((el) => el.nativeElement);

        if (
            !_normalGroupHeadings ||
            !_stickyGroupHeadings ||
            !document.getElementsByClassName('static-group-heading')[0]
        ) {
            return;
        }

        this.leftHeadingPosition = document
            .getElementsByClassName('static-group-heading')[0]
            .getBoundingClientRect().left;

        _normalGroupHeadings.forEach((normalGroupHeading, index) => {
            const normalTop = normalGroupHeading.getBoundingClientRect().top;
            const stickyTop = _stickyGroupHeadings[index].getBoundingClientRect().top;

            if (this.groupHeadingsIsStuckStates) {
                this.groupHeadingsIsStuckStates[index] = normalTop >= stickyTop;
            }
        });
    }

    public isGroupCheckable(group: TableGroup): boolean {
        if (this.checkableRowsService) {
            const rows = this.checkableRowsService.getCachedGroup(group.heading);
            return rows && rows.length > 0;
        } else {
            return false;
        }
    }

    public areRowsCheckable(row: object): boolean {
        if (this.shouldShowCheckbox) {
            return this.shouldShowCheckbox(row);
        }
        return true;
    }
}
